import { useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, Image as ImageIcon, Undo, Redo } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const RichTextEditor = ({ value, onChange }) => {
  const ref = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const sync = () => onChange(ref.current.innerHTML);

  const cmd = (command, arg = null) => {
    ref.current.focus();
    document.execCommand(command, false, arg);
    sync();
  };

  const addLink = () => {
    const url = window.prompt("Adresse du lien (https://...)");
    if (url) cmd("createLink", url);
  };

  const insertImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      cmd("insertImage", `${BACKEND_URL}${data.url}`);
    } catch (err) {
      toast.error("Échec de l'upload de l'image.");
    } finally {
      e.target.value = "";
    }
  };

  const Btn = ({ onClick, title, testid, children }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
    >
      {children}
    </button>
  );

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-100 bg-slate-50 px-2 py-1.5">
        <Btn onClick={() => cmd("bold")} title="Gras" testid="rte-bold"><Bold className="w-4 h-4" /></Btn>
        <Btn onClick={() => cmd("italic")} title="Italique" testid="rte-italic"><Italic className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn onClick={() => cmd("formatBlock", "<h2>")} title="Titre" testid="rte-h2"><Heading2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => cmd("formatBlock", "<h3>")} title="Sous-titre" testid="rte-h3"><Heading3 className="w-4 h-4" /></Btn>
        <Btn onClick={() => cmd("formatBlock", "<p>")} title="Paragraphe" testid="rte-p"><span className="text-xs font-bold">P</span></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn onClick={() => cmd("insertUnorderedList")} title="Liste à puces" testid="rte-ul"><List className="w-4 h-4" /></Btn>
        <Btn onClick={() => cmd("insertOrderedList")} title="Liste numérotée" testid="rte-ol"><ListOrdered className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn onClick={addLink} title="Lien" testid="rte-link"><Link2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => fileRef.current?.click()} title="Insérer une image" testid="rte-image"><ImageIcon className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn onClick={() => cmd("undo")} title="Annuler" testid="rte-undo"><Undo className="w-4 h-4" /></Btn>
        <Btn onClick={() => cmd("redo")} title="Rétablir" testid="rte-redo"><Redo className="w-4 h-4" /></Btn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={insertImage} data-testid="rte-image-input" />
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={sync}
        data-testid="rte-content"
        className="prose-editor min-h-[280px] max-h-[520px] overflow-y-auto p-4 text-slate-800 focus:outline-none"
        suppressContentEditableWarning
      />
    </div>
  );
};
